import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/shared/store/auth'; // Import useAuthStore

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Axios instance configured for the Mira API.
 * Includes automatic token injection, refresh logic, and error handling.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // Send cookies for refresh token
});

// Token refresh state to prevent race conditions
let isRefreshing = false;
let refreshSubscribers: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

function subscribeTokenRefresh(resolve: (token: string) => void, reject: (err: unknown) => void) {
  refreshSubscribers.push({ resolve, reject });
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((sub) => sub.resolve(token));
  refreshSubscribers = [];
}

function onTokenRefreshFailed(err: unknown) {
  refreshSubscribers.forEach((sub) => sub.reject(err));
  refreshSubscribers = [];
}

// Request interceptor - adds authentication token from store
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from Zustand store
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor - unwraps API response and handles 401 with token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Unwrap the API response: {success: true, data: ...} -> data
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      response.data = response.data.data;
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized (skip refresh endpoint to avoid infinite loop)
    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true;

      // If already refreshing, wait for the refresh to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            (err: unknown) => {
              reject(err);
            }
          );
        });
      }

      isRefreshing = true;

      try {
        // Try to refresh the token using the Zustand store action
        await useAuthStore.getState().refreshToken();
        const newAccessToken = useAuthStore.getState().accessToken; // Get new token from store

        isRefreshing = false;
        if (newAccessToken) {
          // Check if new token exists before notifying
          onTokenRefreshed(newAccessToken);
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        } else {
          // Token refresh failed but no explicit error? Logout.
          useAuthStore.getState().logout();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        isRefreshing = false;
        onTokenRefreshFailed(refreshError);
        // Refresh failed - clear auth and redirect to login
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 402 Payment Required — subscription expired
    if (error.response?.status === 402) {
      // Redirect to billing page unless already there
      if (!window.location.pathname.startsWith('/billing')) {
        window.location.href = '/billing';
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
